// src/data/resources.js
// Firestore CRUD for the `resources` collection.
//
// Doc shape:
// {
//   category: string,      // one of the keys in src/config/categories.js
//   kind: 'link' | 'page',
//   title: string,
//   url: string,           // present when kind === 'link'
//   markdown: string,      // present when kind === 'page'
//   description: string,   // optional short subtitle
//   iconUrl: string,       // optional
//   team: string,          // optional (links) — owning/using team, shown in the Third Party table
//   userPointers: string,  // optional (links) — getting-started pointers, one per line (\n)
//   officialGuideUrl: string, // optional (links) — link to official how-to/docs
//   order: number,
//   createdAt: Timestamp,
//   updatedAt: Timestamp,
// }

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

const RESOURCES_COLLECTION = 'resources';

// Ordered by `order` only (a single-field index, auto-created by Firestore —
// no composite index needed). Consumers group results by category
// themselves, using the fixed order from src/config/categories.js — a plain
// orderBy('category') would sort alphabetically by key, which does not match
// the config order anyway.
function resourcesQuery() {
  return query(collection(db, RESOURCES_COLLECTION), orderBy('order'));
}

function docToResource(docSnap) {
  return { id: docSnap.id, ...docSnap.data() };
}

// Subscribes to live updates of all Resources, ordered by category then
// order. Returns an unsubscribe function. `onError` (optional) is called if the
// listener fails — most commonly a Firestore permission-denied when the rules
// don't grant this user read access. Without it, a denied read fails silently
// and the caller can never leave its loading state.
export function subscribeResources(cb, onError) {
  return onSnapshot(
    resourcesQuery(),
    (snapshot) => {
      cb(snapshot.docs.map(docToResource));
    },
    (err) => {
      console.error('subscribeResources failed:', err);
      if (onError) onError(err);
    }
  );
}

// One-off fetch of all Resources, ordered by category then order.
export async function listResources() {
  const snapshot = await getDocs(resourcesQuery());
  return snapshot.docs.map(docToResource);
}

export async function getResource(id) {
  const snap = await getDoc(doc(db, RESOURCES_COLLECTION, id));
  if (!snap.exists()) return null;
  return docToResource(snap);
}

export async function addResource(data) {
  const payload = {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  const ref = await addDoc(collection(db, RESOURCES_COLLECTION), payload);
  return ref.id;
}

export async function updateResource(id, data) {
  const payload = {
    ...data,
    updatedAt: serverTimestamp(),
  };
  await updateDoc(doc(db, RESOURCES_COLLECTION, id), payload);
}

export async function deleteResource(id) {
  await deleteDoc(doc(db, RESOURCES_COLLECTION, id));
}
